import { RootContextBase } from "@skylab-shared/core";
import { DiscountKind, FiscalDeviceModel, SaleAdvanceDocumentItemModel, SaleDocumentItemModel, SaleDocumentKind, TinKind } from "@skylab-shared/dataModel";
import { Suz18Types } from "@skylab-shared/suz";
import { Calc, MidpointRounding } from "@skylab-shared/utils";
import { PaymentMethodQuery } from "../../../payment-method/queries/payment-method.query-base";
import { isAdvance } from "../../../sale/utils/sale-utils";
import { UnitQuery } from "../../../unit/queries/unit.query-base";
import { VatRateForSaleQuery } from "../../../vat/queries/vat-rate-for-sale.query-base";
import { ArticleDiscount, ArticleDiscountType, ArticleVatRate, ArticleVatRateType } from "../../common/suz-article";
import { DocumentToFiscalize, ProductToFiscalize } from "../../common/suz-helper-types";

type ReceiptOperationData = Extract<Suz18Types.OperationBeginData['Operation'], { PrintReceipt: any }>['PrintReceipt']['Args'];
type ReceiptArticleOperationData = ReceiptOperationData['Receipt']['Articles'][number];
type DeviceVisibilityParams = { descriptionVisible: boolean, tinVisible: boolean };

export async function PrintReceiptOperationManager(rootContext: RootContextBase, deviceDetails: Suz18Types.DeviceInfo | undefined, device: FiscalDeviceModel, document: DocumentToFiscalize, products: ProductToFiscalize[]) {
	let preparedReceipt: ReceiptOperationData;
	let error: 'VatRateTechnicalZeroMapping' | 'VatRateMapping' | 'PaymentMethodMapping' | null = null;

	const vatRates = await rootContext.getAsync(VatRateForSaleQuery);
	const units = await rootContext.getAsync(UnitQuery);
	const paymentMethods = await rootContext.getAsync(PaymentMethodQuery);

	function getDeviceVisibilityParams(): DeviceVisibilityParams {
		const booleanStringMap: Record<string, boolean> = {false: false, true: true}; // z SUZA wartość boolean przychodzi w stringu i to jeszcze różnymi casemi, trzeba zmapować

		const description = deviceDetails?.DeviceDefinition?.BusinessParametersValues?.PrintItemDescription;
		const buyerTaxId = deviceDetails?.DeviceDefinition?.BusinessParametersValues?.PrintBuyerTaxId;
		return {
			descriptionVisible: (description && booleanStringMap[description.toLowerCase()]) || false, // dodatkowy opis pozycji
			tinVisible: (buyerTaxId && booleanStringMap[buyerTaxId.toLowerCase()]) || false, // widoczność nip
		};
	}

	function getVatRateInfo(id: number): { vatValue: number | null; vatType: ArticleVatRateType; } {
		const vatRate = vatRates.find(v => v.id === id)!;
		const vatType = (() => {
			if (vatRate.untaxed || vatRate.exempt) return ArticleVatRateType.Exempt;
			if (vatRate.rate >= 0) return ArticleVatRateType.Numeric;
			return ArticleVatRateType.Inactive;
		})();
		const vatValue = vatType === ArticleVatRateType.Numeric ? vatRate.rate : null;
		return {
			vatType,
			vatValue
		};
	}

	function getArticleVatRate(documentKind: SaleDocumentKind, vatRateId: number): ArticleVatRate | undefined {
		// dla faktury marzy uzywamy bezwzględnie skonfigurowanego zera technicznego
		if (documentKind === SaleDocumentKind.VatMarginInvoice) {
			const technicalZero = device?.vatRatesParameters?.technicalZero;
			if (technicalZero) {
				return {
					Value: 0,
					VatCode: technicalZero,
					VatRateType: ArticleVatRateType.Numeric
				};
			}
			error = 'VatRateTechnicalZeroMapping';
			return undefined;
		} else if (device?.vatRatesParameters?.mappings) {
			const mapping = device.vatRatesParameters.mappings.filter(m => m.vatRateId === vatRateId).shift();
			if (mapping != null) {
				const vatRate = getVatRateInfo(vatRateId);
				return {
					Value: vatRate.vatValue,
					VatCode: mapping.mapped,
					VatRateType: vatRate.vatType
				};
			}
			error = 'VatRateMapping';
			return undefined;
		} else {
			const vatRate = getVatRateInfo(vatRateId);
			return {
				Value: vatRate.vatValue,
				VatCode: 0,
				VatRateType: vatRate.vatType,
			};
		}
	}

	function getArticleDiscount(item: SaleDocumentItemModel): ArticleDiscount | null {
		const discount: any = {
			Type: 0,
			Value: 0,
			Description: null
		};
		if (item.discountValue !== 0) {
			discount.Type = ArticleDiscountType.Amount;
			discount.Value = Calc.round(item.value_grossAfterDiscount - item.value_grossBeforeDiscount, 2, MidpointRounding.AwayFromZero);
			discount.Description = item.discountKind === DiscountKind.Percentage ? (item.discountValue * 100).toFixed(2).toString().replace('.', ',') + '%' : null;

			if (discount.Value === 0) return null; // jeśli cena rabatu zaokrąglona wynosi 0
			return discount as ArticleDiscount;
		} else {
			return null;
		}
	}

	function getUnitSymbol(id: number): string {
		return units.find(u => u.id === id)?.symbol || '';
	}

	function mapReceiptOperationParams(receiptData: ReceiptOperationData, deviceVisibilityParams: DeviceVisibilityParams): void {
		if (deviceDetails) {
			const businessParams = deviceDetails.DeviceDefinition.BusinessParametersValues;
			if (businessParams.PrintSystemNumber) { // numer systemowy
				receiptData.Receipt.SystemNumber = document.documentNumber;
			}

			if (businessParams.PrintCashierName) { // nazwa kasjera
				receiptData.Receipt.ExtraData = {
					Cashier: document.issuer,
					CRNumber: '  ',
					FooterLines: []
				};
			}
		}

		if (deviceVisibilityParams.tinVisible) {
			if (document.buyer) {
				if (document.buyer.tinKind === TinKind.NIP) {
					receiptData.Receipt.BuyerTaxId = {
						Value: document.buyer.tin || '',
						Type: 0
					};
				}
			} else if (document.client && document.client.tinKind === TinKind.NIP) {
				receiptData.Receipt.BuyerTaxId = {
					Value: document.client.tin || '',
					Type: 0
				};
			}
		}

		const paymentMethod = paymentMethods.find(p => p.id === document.paymentMethodId);
		if (paymentMethod) {
			const price = isAdvance(document) ? document.advanceValue : document.grossValue;
			const addPayment = (name: string, value: number) => {
				const paymentTerm: any = {
					Description: null,
					Name: name,
					Value: value
				};
				receiptData.Receipt.PaymentTerms.push(paymentTerm)
			}

			if (device?.paymentMethodsParameters?.mappings?.length) {
				const mapping = device.paymentMethodsParameters.mappings.find(m => m.paymentMethodId === document.paymentMethodId);
				if (mapping) {
					addPayment(mapping.mapped, price);
				} else {
					error = 'PaymentMethodMapping';
				}
			} else {
				addPayment(paymentMethod.name, price);
			}
		}
	}

	function mapReceiptOperationArticles(receiptData: ReceiptOperationData, deviceVisibilityParams: DeviceVisibilityParams): void {
		document.items.forEach(item => {
			const articleObject = getBaseReceiptArticleOperationData();
			const articleData = articleObject['InsERT.Devices.Core.OperationsData.FiscalPrinter.Article'];

			const articleVat = getArticleVatRate(document.kind, item.vatRateId);
			if (typeof articleVat === 'undefined') return;
			articleData.Vat = articleVat;

			const isAdvanceInvoice = isAdvance(document);
			const product = products.find(p => p.id === item.productId);
			articleData.Name = product ? product.name : item.productName || '';
			// dla fakury zaliczkowej dodajemy prefix Zaliczka
			if (isAdvanceInvoice) {
				articleData.Name = 'Zaliczka ' + articleData.Name;
			}
			articleData.Discount =  (isAdvanceInvoice ? null : getArticleDiscount(item)) as any;
			articleData.IsVoid = false;
			// dla fakury zaliczkowej uzywamy bezwzględnie 1 przy ilosci
			articleData.Quantity = isAdvanceInvoice ? 1 : item.quantity;
			articleData.Price = isAdvanceInvoice ? (<SaleAdvanceDocumentItemModel>item).advance : item.price_grossBeforeDiscount;
			articleData.Value = null;
			articleData.Unit = getUnitSymbol(item.unitId);

			if (deviceVisibilityParams.descriptionVisible) {
				articleData.CustomFields.Description = item.note;
			}

			receiptData.Receipt.Articles.push(articleObject);
		});
	}

	return {
		prepareData: () => {
			error = null;
			const receiptData = getBaseReceiptOperationData();
			const deviceVisibilityParams = getDeviceVisibilityParams();

			mapReceiptOperationParams(receiptData, deviceVisibilityParams);
			mapReceiptOperationArticles(receiptData, deviceVisibilityParams);

			preparedReceipt = receiptData;
			return preparedReceipt;
		},
		getLastError: () => error
	};
}


function getBaseReceiptArticleOperationData(): ReceiptArticleOperationData {
	return {
		"InsERT.Devices.Core.OperationsData.FiscalPrinter.Article": {
			CustomFields: {
				Description: "",
			},
			Discount: {
				Description: null,
				Type: 0,
				Value: 0,
			},
			IsVoid: false,
			Name: '',
			Price: 0,
			Quantity: 0,
			Unit: null,
			Value: null,
			Vat: {
				Value: 0,
				VatCode: 0,
				VatRateType: 0,
			},
			ValidatedCustomFields: {}
		}
	} as any;
}


function getBaseReceiptOperationData(): ReceiptOperationData {
	return {
		BusinessParamsWithValues: {},
		Receipt: {
			Articles: [],
			BuyerTaxId: null,
			CustomFields: {},
			Discount: null,
			ExtraData: null,
			InvoiceData: null,
			Packages: [],
			PaymentTerms: [],
			SystemNumber: null,
			ValidatedCustomFields: null
		},
	} as any;
}
